/**
 * Inventory Summary Component
 *
 * High-level inventory summary view with:
 * - Filters for all data dimensions
 * - Summary table (Name, Country, Sales Volume, Stock, Days Left, Incoming, Value)
 * - Bar charts for capital invested, days-left, and sales velocity
 */

import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip as MuiTooltip,
  Typography,
} from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { InventoryGrouping, PurchaseOrder } from "models/InventoryModel";
import { formatNumber } from "../utils";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function formatDollar(value: number): string {
  return `$${Math.round(value).toLocaleString()}`;
}

interface InventoryDashboardProps {
  inventoryGroupings: InventoryGrouping[];
  forecastsPayload: any;
  consolidatedPOs: PurchaseOrder[];
  isLoading: boolean;
}

type SortField = "name" | "salesVolume" | "currentStock" | "daysLeft" | "totalIncoming" | "inventoryValue";
type SortDir = "asc" | "desc";

export default function InventoryDashboard({
  inventoryGroupings,
  forecastsPayload,
  consolidatedPOs,
  isLoading,
}: InventoryDashboardProps) {
  const [salesVolumeDays, setSalesVolumeDays] = useState<number>(7);
  const [sortField, setSortField] = useState<SortField>("salesVolume");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Filters
  const [filterProductName, setFilterProductName] = useState("");
  const [filterCountries, setFilterCountries] = useState<string[]>([]);
  const [filterSalesVolumeMin, setFilterSalesVolumeMin] = useState("");
  const [filterSalesVolumeMax, setFilterSalesVolumeMax] = useState("");
  const [filterStockMin, setFilterStockMin] = useState("");
  const [filterStockMax, setFilterStockMax] = useState("");
  const [filterDaysLeftMin, setFilterDaysLeftMin] = useState("");
  const [filterDaysLeftMax, setFilterDaysLeftMax] = useState("");
  const [filterIncomingMin, setFilterIncomingMin] = useState("");
  const [filterIncomingMax, setFilterIncomingMax] = useState("");
  const [filterValueMin, setFilterValueMin] = useState("");
  const [filterValueMax, setFilterValueMax] = useState("");

  // Build dashboard data from forecasts
  const dashboardItems = useMemo(() => {
    if (!forecastsPayload?.groupings) return [];

    const forecastsByKey = new Map<string, any>();
    for (const fg of forecastsPayload.groupings) {
      forecastsByKey.set(fg.key, fg);
    }

    // Build total incoming by inventory_id from POs. Per line, not per PO: ShipBob
    // receives a PO one line at a time, and a partly stowed line only has its
    // remainder still to come (the server nets that into incomingQuantity).
    const incomingByInventoryId: Record<string, number> = {};
    for (const po of consolidatedPOs) {
      for (const li of po.lineItems) {
        const incoming = li.incomingQuantity ?? (li.received ? 0 : li.quantity);
        incomingByInventoryId[li.inventoryId] = (incomingByInventoryId[li.inventoryId] || 0) + incoming;
      }
    }

    return inventoryGroupings.map((grouping) => {
      const forecast = forecastsByKey.get(grouping.key);
      const dailyRunRate = forecast?.dailyRunRate ?? 0;
      const currentStock = forecast?.currentInventory ?? 0;
      const salesVolume = dailyRunRate * salesVolumeDays;
      const daysLeft = dailyRunRate > 0 ? Math.round(currentStock / dailyRunRate) : null;
      const ddpPrice = grouping.ddpPrice ?? forecast?.ddpPrice ?? 0;
      // A family row has no POs or price of its own: its members' incoming (in units of the single
      // item) and their stock at each member's own price
      const familyMembers = grouping.family?.members;
      const totalIncoming = familyMembers
        ? familyMembers.reduce((sum, member) => sum + (incomingByInventoryId[member.inventoryId] || 0) * member.qty, 0)
        : incomingByInventoryId[grouping.inventory_id] || 0;
      const inventoryValue = Math.round(familyMembers
        ? familyMembers.reduce((sum, member) => sum + member.currentInventory * (forecastsByKey.get(member.key)?.ddpPrice ?? 0), 0)
        : currentStock * ddpPrice);

      return {
        key: grouping.key,
        // A family row stands for its single item and packs together
        inventoryId: grouping.family ? `all packs of ${grouping.family.baseInventoryId}` : grouping.inventory_id,
        name: grouping.inventory_name || grouping.inventory_id,
        country: grouping.country,
        brand: grouping.brand,
        dailyRunRate,
        salesVolume,
        currentStock,
        daysLeft,
        totalIncoming,
        inventoryValue,
        ddpPrice,
      };
    });
  }, [inventoryGroupings, forecastsPayload, consolidatedPOs, salesVolumeDays]);

  // Extract unique countries for filter dropdown
  const allCountries = useMemo(() => {
    const countries = new Set<string>();
    for (const item of dashboardItems) {
      countries.add(item.country);
    }
    return Array.from(countries).sort();
  }, [dashboardItems]);

  // Apply filters
  const filteredItems = useMemo(() => {
    const nameFilter = filterProductName.trim().toLowerCase();
    const svMin = filterSalesVolumeMin ? parseFloat(filterSalesVolumeMin) : null;
    const svMax = filterSalesVolumeMax ? parseFloat(filterSalesVolumeMax) : null;
    const stMin = filterStockMin ? parseFloat(filterStockMin) : null;
    const stMax = filterStockMax ? parseFloat(filterStockMax) : null;
    const dlMin = filterDaysLeftMin ? parseFloat(filterDaysLeftMin) : null;
    const dlMax = filterDaysLeftMax ? parseFloat(filterDaysLeftMax) : null;
    const inMin = filterIncomingMin ? parseFloat(filterIncomingMin) : null;
    const inMax = filterIncomingMax ? parseFloat(filterIncomingMax) : null;
    const vMin = filterValueMin ? parseFloat(filterValueMin) : null;
    const vMax = filterValueMax ? parseFloat(filterValueMax) : null;

    return dashboardItems.filter((item) => {
      if (nameFilter && !item.name.toLowerCase().includes(nameFilter) && !item.inventoryId.includes(nameFilter)) return false;
      if (filterCountries.length > 0 && !filterCountries.includes(item.country)) return false;
      if (svMin != null && item.salesVolume < svMin) return false;
      if (svMax != null && item.salesVolume > svMax) return false;
      if (stMin != null && item.currentStock < stMin) return false;
      if (stMax != null && item.currentStock > stMax) return false;
      if (dlMin != null && (item.daysLeft == null || item.daysLeft < dlMin)) return false;
      if (dlMax != null && (item.daysLeft == null || item.daysLeft > dlMax)) return false;
      if (inMin != null && item.totalIncoming < inMin) return false;
      if (inMax != null && item.totalIncoming > inMax) return false;
      if (vMin != null && item.inventoryValue < vMin) return false;
      if (vMax != null && item.inventoryValue > vMax) return false;
      return true;
    });
  }, [dashboardItems, filterProductName, filterCountries, filterSalesVolumeMin, filterSalesVolumeMax, filterStockMin, filterStockMax, filterDaysLeftMin, filterDaysLeftMax, filterIncomingMin, filterIncomingMax, filterValueMin, filterValueMax]);

  // Sort filtered items
  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case "name": cmp = a.name.localeCompare(b.name); break;
        case "salesVolume": cmp = a.salesVolume - b.salesVolume; break;
        case "currentStock": cmp = a.currentStock - b.currentStock; break;
        case "daysLeft": cmp = (a.daysLeft ?? 9999) - (b.daysLeft ?? 9999); break;
        case "totalIncoming": cmp = a.totalIncoming - b.totalIncoming; break;
        case "inventoryValue": cmp = a.inventoryValue - b.inventoryValue; break;
      }
      return sortDir === "desc" ? -cmp : cmp;
    });
  }, [filteredItems, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  // Chart data: Top 15 by capital invested (uses filtered items)
  const capitalChartData = useMemo(() => {
    const top = [...filteredItems]
      .filter((item) => item.inventoryValue > 0)
      .sort((a, b) => b.inventoryValue - a.inventoryValue)
      .slice(0, 15);

    return {
      labels: top.map((item) => item.name.slice(0, 30)),
      datasets: [{
        label: "Inventory Value",
        data: top.map((item) => item.inventoryValue),
        backgroundColor: "rgba(59, 130, 246, 0.7)",
        borderRadius: 3,
      }],
    };
  }, [filteredItems]);

  // Chart data: Days Left (show items with low days-left first)
  const daysLeftChartData = useMemo(() => {
    const items = [...filteredItems]
      .filter((item) => item.daysLeft != null && item.dailyRunRate > 0)
      .sort((a, b) => (a.daysLeft ?? 0) - (b.daysLeft ?? 0))
      .slice(0, 20);

    return {
      labels: items.map((item) => item.name.slice(0, 30)),
      datasets: [{
        label: "Days of Stock Left",
        data: items.map((item) => item.daysLeft ?? 0),
        backgroundColor: items.map((item) => {
          const days = item.daysLeft ?? 0;
          if (days < 30) return "rgba(239, 68, 68, 0.7)";
          if (days < 90) return "rgba(245, 158, 11, 0.7)";
          return "rgba(34, 197, 94, 0.7)";
        }),
        borderRadius: 3,
      }],
    };
  }, [filteredItems]);

  // Chart data: Sales velocity
  const velocityChartData = useMemo(() => {
    const top = [...filteredItems]
      .filter((item) => item.dailyRunRate > 0)
      .sort((a, b) => b.dailyRunRate - a.dailyRunRate)
      .slice(0, 15);

    return {
      labels: top.map((item) => item.name.slice(0, 30)),
      datasets: [{
        label: "Units/Day",
        data: top.map((item) => Math.round(item.dailyRunRate * 10) / 10),
        backgroundColor: "rgba(124, 58, 237, 0.5)",
        borderColor: "rgba(124, 58, 237, 1)",
        borderWidth: 1,
        borderRadius: 3,
      }],
    };
  }, [filteredItems]);

  const capitalBarOptions = {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => formatDollar(ctx.raw),
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: { callback: (v: any) => formatDollar(v) },
      },
    },
  };

  const daysLeftBarOptions = {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      x: { beginAtZero: true },
    },
  };

  const velocityBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      // datalabels disabled
    },
    scales: {
      y: { beginAtZero: true },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 20,
          font: { size: 10 },
          color: "#333",
        },
      },
    },
  };

  // Helper for min/max filter inputs
  const rangeFilter = (label: string, minVal: string, maxVal: string, setMin: (v: string) => void, setMax: (v: string) => void, width = 70) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography variant="caption" sx={{ opacity: 0.7, minWidth: 50, fontSize: "0.7rem" }}>{label}</Typography>
      <TextField size="small" placeholder="Min" value={minVal} onChange={(e) => setMin(e.target.value)}
        type="number" inputProps={{ style: { padding: "4px 6px", fontSize: "0.75rem" } }} sx={{ width }} />
      <Typography variant="caption" sx={{ opacity: 0.5 }}>-</Typography>
      <TextField size="small" placeholder="Max" value={maxVal} onChange={(e) => setMax(e.target.value)}
        type="number" inputProps={{ style: { padding: "4px 6px", fontSize: "0.75rem" } }} sx={{ width }} />
    </Box>
  );

  if (isLoading && dashboardItems.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <Typography>Loading summary...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, opacity: 0.7 }}>Filters</Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
          {/* Product name search */}
          <TextField
            size="small"
            label="Product Name / ID"
            value={filterProductName}
            onChange={(e) => setFilterProductName(e.target.value)}
            placeholder="Search..."
            sx={{ minWidth: 200 }}
            inputProps={{ style: { padding: "6px 10px", fontSize: "0.85rem" } }}
          />

          {/* Country */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Country</InputLabel>
            <Select
              multiple
              value={filterCountries}
              onChange={(e) => {
                const v = e.target.value;
                setFilterCountries(typeof v === "string" ? v.split(",") : v);
              }}
              input={<OutlinedInput label="Country" />}
              renderValue={(sel) => sel.length === allCountries.length ? "All" : sel.join(", ")}
            >
              {allCountries.map((c) => (
                <MenuItem key={c} value={c}>
                  <Checkbox checked={filterCountries.includes(c)} />
                  <ListItemText primary={c} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Sales Volume Period */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Sales Volume Period</InputLabel>
            <Select
              value={salesVolumeDays}
              label="Sales Volume Period"
              onChange={(e) => setSalesVolumeDays(Number(e.target.value))}
            >
              <MenuItem value={7}>Last 7 days avg</MenuItem>
              <MenuItem value={14}>Last 14 days avg</MenuItem>
              <MenuItem value={30}>Last 30 days avg</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="body2" sx={{ opacity: 0.7, alignSelf: "center", ml: "auto" }}>
            {filteredItems.length} / {dashboardItems.length} products
          </Typography>
        </Box>

        {/* Range filters */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1.5 }}>
          {rangeFilter("Sales Vol", filterSalesVolumeMin, filterSalesVolumeMax, setFilterSalesVolumeMin, setFilterSalesVolumeMax)}
          {rangeFilter("Stock", filterStockMin, filterStockMax, setFilterStockMin, setFilterStockMax)}
          {rangeFilter("Days Left", filterDaysLeftMin, filterDaysLeftMax, setFilterDaysLeftMin, setFilterDaysLeftMax)}
          {rangeFilter("Incoming", filterIncomingMin, filterIncomingMax, setFilterIncomingMin, setFilterIncomingMax)}
          {rangeFilter("Value ($)", filterValueMin, filterValueMax, setFilterValueMin, setFilterValueMax)}
        </Box>
      </Paper>

      {/* Summary Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>
                <TableSortLabel
                  active={sortField === "name"}
                  direction={sortField === "name" ? sortDir : "asc"}
                  onClick={() => handleSort("name")}
                >
                  Product
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={sortField === "salesVolume"}
                  direction={sortField === "salesVolume" ? sortDir : "desc"}
                  onClick={() => handleSort("salesVolume")}
                >
                  Sales Vol ({salesVolumeDays}d)
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={sortField === "currentStock"}
                  direction={sortField === "currentStock" ? sortDir : "desc"}
                  onClick={() => handleSort("currentStock")}
                >
                  Current Stock
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={sortField === "daysLeft"}
                  direction={sortField === "daysLeft" ? sortDir : "desc"}
                  onClick={() => handleSort("daysLeft")}
                >
                  Days Left
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <MuiTooltip title="Units still to come across open POs: what was ordered, less anything ShipBob has already stowed or that was marked received. Stowed units are excluded because they are already counted in Current Stock.">
                  <span>
                    <TableSortLabel
                      active={sortField === "totalIncoming"}
                      direction={sortField === "totalIncoming" ? sortDir : "desc"}
                      onClick={() => handleSort("totalIncoming")}
                    >
                      Total Incoming
                    </TableSortLabel>
                  </span>
                </MuiTooltip>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={sortField === "inventoryValue"}
                  direction={sortField === "inventoryValue" ? sortDir : "desc"}
                  onClick={() => handleSort("inventoryValue")}
                >
                  Inventory Value
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedItems.map((item) => (
              <TableRow key={item.key} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.8rem" }}>
                    {item.name}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>
                    {item.inventoryId}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {item.country}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {formatNumber(Math.round(item.salesVolume))}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(item.currentStock)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color: item.daysLeft != null && item.daysLeft < 30
                      ? "error.main"
                      : item.daysLeft != null && item.daysLeft < 90
                        ? "warning.main"
                        : "inherit",
                    fontWeight: item.daysLeft != null && item.daysLeft < 30 ? 600 : undefined,
                  }}
                >
                  {item.daysLeft != null ? formatNumber(item.daysLeft) : "\u2014"}
                </TableCell>
                <TableCell align="right">
                  {item.totalIncoming > 0 ? formatNumber(item.totalIncoming) : "\u2014"}
                </TableCell>
                <TableCell align="right">
                  {item.inventoryValue > 0 ? formatDollar(item.inventoryValue) : "\u2014"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Charts */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
        {/* Capital Invested */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Capital Invested per Product
          </Typography>
          <Box sx={{ height: 400 }}>
            <Bar data={capitalChartData} options={capitalBarOptions} />
          </Box>
        </Paper>

        {/* Days Left */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Days of Stock Left
          </Typography>
          <Box sx={{ height: 400 }}>
            <Bar data={daysLeftChartData} options={daysLeftBarOptions} />
          </Box>
        </Paper>
      </Box>

      {/* Sales Velocity */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Sales Velocity (Units/Day)
        </Typography>
        <Box sx={{ height: 350 }}>
          <Bar data={velocityChartData} options={velocityBarOptions} />
        </Box>
      </Paper>
    </Box>
  );
}
